import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { apiFetch, ApiError } from "@/api/fetch";
import { Button } from "@/components/ui/button";
import { useSession } from "@/lib/auth/session";
import { getLastOrgSlug } from "@/lib/org/OrgContext";

interface LandingOrgSummary {
  id: string;
  slug: string;
  name: string;
  role: string;
}
interface LandingResponse {
  organizations: LandingOrgSummary[];
}

const landingQuery = {
  queryKey: ["landing"] as const,
  queryFn: () => apiFetch<LandingResponse>("/api/users/me/landing"),
};

// Bare root — sends authed users straight into their workspace (cached org if
// available, otherwise the first one they belong to). Unauthed users go to the
// Keycloak sign-in redirect; /login is reserved for explicit-entry / post-
// signout / error-retry.
//
// The dedicated org-picker page (/orgs) is gone — org switching now happens
// inline via the workspace shell's sidebar dropdown. Only the no-orgs branch
// and the landing-fetch error branch ever render this route's component.
export const Route = createFileRoute("/")({
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated()) {
      // Fire-and-forget: the browser navigates to Keycloak. The component
      // below renders a brief splash while the navigation kicks in. If signin
      // fails to even initiate, bounce to /login so the user can retry.
      Promise.resolve(context.auth.signinRedirect()).catch(() => {
        window.location.replace("/login");
      });
      return;
    }

    // ensureQueryData throws on network/HTTP failure. The errorComponent below
    // catches it and renders a retry card so the user isn't dropped onto a
    // bare crash screen with no way out.
    const landing = await context.queryClient.ensureQueryData(landingQuery);

    if (landing.organizations.length === 0) {
      // Let the component render the no-orgs error.
      return;
    }

    const cachedSlug = getLastOrgSlug();
    const chosen =
      (cachedSlug && landing.organizations.find((o) => o.slug === cachedSlug)) ||
      landing.organizations[0];

    throw redirect({ to: "/o/$slug", params: { slug: chosen.slug } });
  },
  component: LandingFallback,
  errorComponent: LandingErrorBoundary,
});

function LandingFallback() {
  const session = useSession();
  const queryClient = useQueryClient();
  const router = useRouter();
  const navigate = useNavigate();

  // Unauthed users see the splash; the signin redirect was already kicked off
  // in beforeLoad. Authed users land here only when they're not a member of
  // any org.
  if (!session.isAuthenticated) {
    return (
      <div className="grid min-h-screen place-items-center bg-muted/30 p-6">
        <div className="w-full max-w-sm rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground shadow-sm">
          Weiterleitung zur Anmeldung …
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-screen place-items-center bg-muted/30 p-6">
      <div className="w-full max-w-md rounded-lg border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          <h1 className="text-lg font-semibold">Keine Organisation</h1>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Du gehörst noch keiner Organisation an. Lass dich von einem Admin
          einladen oder lege selbst eine an.
        </p>
        <Button
          variant="outline"
          className="mt-6 w-full"
          onClick={() => {
            queryClient.clear();
            void session.signout();
          }}
        >
          Abmelden
        </Button>
        <Button
          variant="ghost"
          className="mt-2 w-full"
          onClick={async () => {
            // Drop the cached empty-org result so the retry actually re-checks
            // membership instead of replaying the same answer for `staleTime`.
            queryClient.removeQueries({ queryKey: landingQuery.queryKey });
            // Re-run beforeLoad so the retry can redirect into a workspace if
            // the user has since been added to an org.
            await router.invalidate();
            void navigate({ to: "/" });
          }}
        >
          Erneut versuchen
        </Button>
      </div>
    </div>
  );
}

function LandingErrorBoundary({ error }: { error: Error }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const router = useRouter();
  const status = error instanceof ApiError ? error.status : null;

  return (
    <div className="grid min-h-screen place-items-center bg-muted/30 p-6">
      <div className="w-full max-w-md rounded-lg border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          <h1 className="text-lg font-semibold">Etwas ist schiefgelaufen</h1>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          {status === 401
            ? "Nicht angemeldet."
            : status === 403
              ? "Keine Berechtigung."
              : (error.message ?? "Unbekannter Fehler.")}
        </p>
        <Button
          className="mt-6 w-full"
          onClick={async () => {
            queryClient.removeQueries({ queryKey: landingQuery.queryKey });
            await router.invalidate();
          }}
        >
          Erneut versuchen
        </Button>
        <Button
          variant="outline"
          className="mt-2 w-full"
          onClick={() => {
            queryClient.clear();
            void session.signout();
          }}
        >
          Abmelden
        </Button>
      </div>
    </div>
  );
}
